import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  CreateRegistrationSessionResult,
  GetRegistrationStatusResult,
  RegistrationStatus,
} from "@/domain/model/Registration";
import type { RegistrationService } from "@/domain/port/RegistrationService";
import type { RegistrationOutcomeUiState } from "./Registration";
import type { CompleteRegistrationAuthentication } from "@/domain/usecase/CompleteRegistrationAuthentication";

type UseRegistrationSuccessViewModelArgs = {
  registrationId: string | undefined;
  registrationService: RegistrationService;
  completeRegistrationAuthentication: CompleteRegistrationAuthentication;
  onAuthenticated: () => void;
};

type RegistrationSuccessViewModel = {
  state: {
    uiState: RegistrationOutcomeUiState;
  };
  intents: {
    retryNow: () => void;
    goToRegistration: () => void;
  };
};

const POLL_FALLBACK_MS = 2_000;
const ERROR_RETRY_MS = 3_000;
const MAX_CONSECUTIVE_ERRORS = 5;

export function useRegistrationSuccessViewModel({
  registrationId,
  registrationService,
  completeRegistrationAuthentication,
  onAuthenticated,
}: UseRegistrationSuccessViewModelArgs): RegistrationSuccessViewModel {
  const [uiState, setUiState] = useState<RegistrationOutcomeUiState>({
    status: "loading",
    title: "Registrierung wird geladen",
    message: "Bitte warten…",
  });

  const timeoutRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  const consecutiveErrorsRef = useRef(0);

  const clearScheduledPoll = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const schedulePoll = useCallback(
    (delayMs: number, fn: () => void) => {
      clearScheduledPoll();
      timeoutRef.current = window.setTimeout(fn, delayMs);
    },
    [clearScheduledPoll],
  );

  const finalizeAuthenticatedSession = useCallback(
    (session: CreateRegistrationSessionResult) => {
      completeRegistrationAuthentication.execute(session);

      setUiState({
        status: "success",
        title: "Konto erfolgreich erstellt",
        message: "Sie werden angemeldet…",
        accountId: session.accountId,
      });

      onAuthenticated();
    },
    [onAuthenticated, completeRegistrationAuthentication],
  );

  const mapWaitingState = useCallback(
    (status: RegistrationStatus): RegistrationOutcomeUiState => {
      switch (status) {
        case "INITIATED":
        case "CHECKOUT_CREATED":
          return {
            status: "waiting",
            title: "Zahlung wird verarbeitet",
            message:
              "Ihre Zahlung wird noch bestätigt. Diese Seite aktualisiert sich automatisch.",
            registrationStatus: status,
          };
        case "CHECKOUT_COMPLETED":
          return {
            status: "waiting",
            title: "Registrierung wird vorbereitet",
            message:
              "Ihre Zahlung wurde bestätigt. Ihr Konto wird jetzt vorbereitet.",
            registrationStatus: status,
          };
        case "ACTIVATING":
          return {
            status: "waiting",
            title: "Konto wird aktiviert",
            message:
              "Fast geschafft. Ihr Konto und Ihr erster Benutzer werden gerade angelegt.",
            registrationStatus: status,
          };
        default:
          return {
            status: "loading",
            title: "Registrierung wird geladen",
            message: "Bitte warten…",
          };
      }
    },
    [],
  );

  const mapTerminalErrorState = useCallback(
    (result: GetRegistrationStatusResult): RegistrationOutcomeUiState => {
      switch (result.status) {
        case "FAILED":
          return {
            status: "error",
            title: "Registrierung fehlgeschlagen",
            message:
              result.message ??
              "Ihre Registrierung konnte nicht abgeschlossen werden.",
            canRetry: result.canRetry,
            registrationStatus: result.status,
          };
        case "EXPIRED":
          return {
            status: "error",
            title: "Checkout abgelaufen",
            message:
              result.message ??
              "Die Checkout-Sitzung ist abgelaufen. Bitte starten Sie den Vorgang erneut.",
            canRetry: result.canRetry,
            registrationStatus: result.status,
          };
        case "CANCELED":
          return {
            status: "error",
            title: "Checkout abgebrochen",
            message: result.message ?? "Der Bezahlvorgang wurde abgebrochen.",
            canRetry: result.canRetry,
            registrationStatus: result.status,
          };
        default:
          return {
            status: "error",
            title: "Unbekannter Status",
            message:
              "Der Status der Registrierung konnte nicht verarbeitet werden.",
            canRetry: false,
            registrationStatus: result.status,
          };
      }
    },
    [],
  );

  const pollStatus = useCallback(async () => {
    if (!registrationId || stoppedRef.current) {
      setUiState({
        status: "error",
        title: "Registrierung nicht gefunden",
        message: "Es wurde keine gültige Registrierungs-ID gefunden.",
        canRetry: false,
      });
      return;
    }

    try {
      const result =
        await registrationService.getRegistrationStatus(registrationId);

      consecutiveErrorsRef.current = 0;

      switch (result.status) {
        case "ACTIVE": {
          const session =
            await registrationService.createRegistrationSession(registrationId);
          finalizeAuthenticatedSession(session);
          return;
        }

        case "INITIATED":
        case "CHECKOUT_CREATED":
        case "CHECKOUT_COMPLETED":
        case "ACTIVATING": {
          setUiState(mapWaitingState(result.status));
          schedulePoll(result.pollAfterMs ?? POLL_FALLBACK_MS, () => {
            void pollStatus();
          });
          return;
        }

        case "FAILED":
        case "EXPIRED":
        case "CANCELED": {
          setUiState(mapTerminalErrorState(result));
          return;
        }

        default: {
          setUiState({
            status: "error",
            title: "Unbekannter Status",
            message:
              "Die Registrierung konnte nicht eindeutig verarbeitet werden.",
            canRetry: false,
          });
        }
      }
    } catch {
      consecutiveErrorsRef.current += 1;

      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        setUiState({
          status: "error",
          title: "Verbindung unterbrochen",
          message:
            "Der Registrierungsstatus konnte mehrfach nicht geladen werden. Bitte versuchen Sie es erneut.",
          canRetry: true,
        });
        return;
      }

      setUiState({
        status: "waiting",
        title: "Verbindung wird erneut aufgebaut",
        message:
          "Der aktuelle Registrierungsstatus konnte gerade nicht geladen werden. Es wird automatisch erneut versucht.",
        registrationStatus: "ACTIVATING",
      });

      schedulePoll(ERROR_RETRY_MS, () => {
        void pollStatus();
      });
    }
  }, [
    finalizeAuthenticatedSession,
    mapTerminalErrorState,
    mapWaitingState,
    registrationId,
    registrationService,
    schedulePoll,
  ]);

  useEffect(() => {
    stoppedRef.current = false;
    void pollStatus();

    return () => {
      stoppedRef.current = true;
      clearScheduledPoll();
    };
  }, [clearScheduledPoll, pollStatus]);

  const retryNow = useCallback(() => {
    consecutiveErrorsRef.current = 0;
    setUiState({
      status: "loading",
      title: "Registrierung wird geladen",
      message: "Bitte warten…",
    });
    void pollStatus();
  }, [pollStatus]);

  const goToRegistration = useCallback(() => {
    window.location.assign("/signup");
  }, []);

  return useMemo(
    () => ({
      state: {
        uiState,
      },
      intents: {
        retryNow,
        goToRegistration,
      },
    }),
    [goToRegistration, retryNow, uiState],
  );
}
